import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Componente Timeline para mostrar la trazabilidad de campos dinámicos
 *
 * Props:
 * - historial: Array del historial completo de valores (desde obtenerHistorialCompleto)
 * - compacto: Si es true, muestra versión compacta
 */
const TimelineTrazabilidad = ({ historial = [], compacto = false }) => {
  const formatearFecha = (fecha) => {
    try {
      return format(new Date(fecha), "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es });
    } catch (error) {
      return fecha;
    }
  };

  const formatearValor = (valor, tipoCampo) => {
    if (valor === null || valor === undefined) return '-';

    switch (tipoCampo) {
      case 'fecha':
        try {
          return format(new Date(valor), 'd/MM/yyyy', { locale: es });
        } catch {
          return valor;
        }

      case 'fecha_hora':
        try {
          return format(new Date(valor), "d/MM/yyyy HH:mm", { locale: es });
        } catch {
          return valor;
        }

      case 'booleano':
        return valor ? 'Sí' : 'No';

      case 'decimal':
      case 'numero':
        return typeof valor === 'number' ? valor.toFixed(2) : valor;

      case 'ubicacion':
        if (typeof valor === 'object' && valor.lat && valor.lng) {
          return `${valor.lat}, ${valor.lng}`;
        }
        return JSON.stringify(valor);

      case 'seleccion_multiple':
        return Array.isArray(valor) ? valor.join(', ') : valor;

      default:
        return String(valor);
    }
  };

  const getColorEstado = (colorHex) => {
    return colorHex || '#2196F3';
  };

  if (!historial || historial.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <EmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            No hay historial de valores registrado para esta orden
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Timeline position="right">
      {historial.map((estadoData, estadoIndex) => (
        <TimelineItem key={`estado-${estadoIndex}`}>
          <TimelineOppositeContent sx={{ flex: 0.3, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Estado {estadoIndex + 1}
            </Typography>
          </TimelineOppositeContent>

          <TimelineSeparator>
            <TimelineDot
              sx={{
                bgcolor: getColorEstado(estadoData.color_estado),
                width: 32,
                height: 32
              }}
            >
              {estadoData.valores && estadoData.valores.length > 0 ? (
                <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
              ) : (
                <EmptyIcon sx={{ color: 'white', fontSize: 20 }} />
              )}
            </TimelineDot>
            {estadoIndex < historial.length - 1 && <TimelineConnector />}
          </TimelineSeparator>

          <TimelineContent sx={{ py: 2 }}>
            <Card
              elevation={2}
              sx={{
                mb: 2,
                borderLeft: 4,
                borderColor: getColorEstado(estadoData.color_estado)
              }}
            >
              <CardContent>
                {/* Header del estado */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {estadoData.descripcion_estado}
                  </Typography>
                  <Chip
                    label={`${estadoData.valores?.length || 0} campo(s) completado(s)`}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Valores del estado */}
                {estadoData.valores && estadoData.valores.length > 0 ? (
                  <Grid container spacing={2}>
                    {estadoData.valores.map((valor, valorIndex) => (
                      <Grid item xs={12} key={`valor-${valorIndex}`}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: 'background.default',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          {/* Nombre del campo */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <DescriptionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {valor.etiqueta_display}
                            </Typography>
                            <Chip
                              label={valor.tipo_campo}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 'auto' }}
                            />
                          </Box>

                          {/* Valor */}
                          <Typography
                            variant="body1"
                            sx={{
                              pl: 3,
                              color: 'text.primary',
                              fontWeight: 500
                            }}
                          >
                            {formatearValor(valor.valor, valor.tipo_campo)}
                          </Typography>

                          {/* Metadata */}
                          {!compacto && (
                            <Box sx={{ display: 'flex', gap: 2, mt: 1.5, pl: 3 }}>
                              {valor.registrado_por && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {valor.registrado_por}
                                  </Typography>
                                </Box>
                              )}
                              {valor.fecha_registro && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatearFecha(valor.fecha_registro)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No se completaron campos en este estado
                  </Typography>
                )}
              </CardContent>
            </Card>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
};

export default TimelineTrazabilidad;
